import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { BarChartDataItem, PieChartDataItem } from '../../../charts/chart.models';
import { Subject } from 'rxjs';
import {
  CommunicationRecipientStatus,
  MassNotification,
  MassNotificationStats,
  NotificationResponseStatus,
  ConfirmationType,
  MassNotificationTemplate
} from 'src/app/modules/communications/store/communications/communications.model';
import { v4 as uuidv4 } from 'uuid';
import { ChannelSelectionService } from 'src/app/modules/communications/services/channel-selection-service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationStatusService } from 'src/app/modules/communications/services/notification-status-service';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
@Component({
    selector: 'topo-notification-response-status-chart-widget',
    templateUrl: './response-status-chart-widget.component.html',
    styleUrls: ['./response-status-chart-widget.component.scss'],
    standalone: false
})
export class ResponseStatusChartWidgetComponent implements OnInit, OnDestroy {
  data: BarChartDataItem[];
  chartType: string = 'doughnut';
  showLegend: boolean = true;
  showPercentage: boolean = false;
  showValues: boolean = false;
  private destroy$ = new Subject<void>();

  @Input() width: string;

  @Input() notification: MassNotification;
  @Input() totalRecipients: number;

  _stats: MassNotificationStats;
  @Input() set notificationStats(value: MassNotificationStats) {
    this._stats = value;
    this.loadRecipientStats(value);
  }

  _template: MassNotificationTemplate;
  @Input() set template(value: MassNotificationTemplate) {
    this._template = value;
  }

  channels: { uid: string; name: string; icon: string }[] = [];
  selectedChannel: { uid: string; name: string; icon: string } = null;

  @ViewChild('canvas') canvas: ElementRef;

  constructor(
    private cultureService: CultureService,
    private translateService: TranslateService,
    private notificationStatusService: NotificationStatusService,
    private templateDesignerService: TemplateDesignerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRecipientStats(stats: MassNotificationStats): void {
    this.showPercentage = true;
    this.showValues = true;

    const responseSets: { status: any; label: string; count: number }[] = [];
    const otherSets: { status: any; label: string; count: number }[] = [];
    const notRespondedSets: { status: any; label: string; count: number }[] = [];

    if (!!!stats?.responsesStats?.responseCounts) {
      const stat = {
        status: CommunicationRecipientStatus.None,
        label: this.translateService.instant('COMMUNICATIONS.RECIPIENT-RESPONSE-STATUS-' + NotificationResponseStatus.None),
        count: 1
      };
      notRespondedSets.push(stat);
    }

    const currentCulture = this.cultureService.currentLang;

    let respondedCount = 0;
    stats?.responsesStats?.responseCounts?.forEach(c => {
      if (!!c) {
        if (!!c.confirmationOptionsUId) {
          const option = this.notification?.messageSettings?.confirmationButtons?.find(b => b.uId === c.confirmationOptionsUId);
          if (option) {
            let status =
              option.type === ConfirmationType.Positive
                ? NotificationResponseStatus.RespondedPositive
                : NotificationResponseStatus.RespondedNegative;
            let label = this.templateDesignerService.getTranslateText(option.text, currentCulture);
            let stat = responseSets.find(v => v.status === status);
            if (!!!stat) {
              stat = { status: status, label: label, count: c.count };
              responseSets.push(stat);
            } else {
              stat.count += c.count;
            }
          } else {
            let status = NotificationResponseStatus.Responded;
            let label = this.translateService.instant('COMMUNICATIONS.RECIPIENT-RESPONSE-STATUS-' + NotificationResponseStatus.Responded);
            let stat = otherSets.find(v => v.status === status);
            if (!!!stat) {
              stat = { status: status, label: label, count: c.count };
              otherSets.push(stat);
            } else {
              stat.count += c.count;
            }
          }
          respondedCount += c.count;
        } else {
          let stat = notRespondedSets.find(v => v.status === NotificationResponseStatus.None);
          let label = this.translateService.instant('COMMUNICATIONS.RECIPIENT-RESPONSE-STATUS-' + NotificationResponseStatus.None);
          if (!!!stat) {
            stat = { status: NotificationResponseStatus.None, label: label, count: c.count };
            notRespondedSets.push(stat);
          } else {
            stat.count += c.count;
          }
          respondedCount += c.count;
        }
      }
    });

    if (respondedCount < this.totalRecipients) {
      let stat = notRespondedSets.find(v => v.status === NotificationResponseStatus.None);
      let label = this.translateService.instant('COMMUNICATIONS.RECIPIENT-RESPONSE-STATUS-' + NotificationResponseStatus.None);
      if (!!!stat) {
        stat = { status: NotificationResponseStatus.None, label: label, count: this.totalRecipients - respondedCount };
        notRespondedSets.push(stat);
      } else {
        stat.count += this.totalRecipients - respondedCount;
      }
    }
    const dataItems = [];
    responseSets.forEach(s => {
      if (s.count) {
        const data = {
          id: uuidv4(),
          data: [],
          label: s.label,
          label2: null
        };
        data.data.push({
          id: s.status.toString(),
          count: s.count,
          color: this.notificationStatusService.getRecipientResponseStatusColor(s.status),
          textColor: '#404040',
          label: s.label
        });
        dataItems.push(data);
      }
    });
    otherSets.forEach(s => {
      if (s.count) {
        const data = {
          id: uuidv4(),
          data: [],
          label: s.label,
          label2: null
        };
        data.data.push({
          id: s.status.toString(),
          count: s.count,
          color: this.notificationStatusService.getRecipientResponseStatusColor(s.status),
          textColor: '#404040',
          label: s.label
        });
        dataItems.push(data);
      }
    });
    notRespondedSets.forEach(s => {
      if (s.count) {
        const data = {
          id: uuidv4(),
          data: [],
          label: s.label,
          label2: null
        };
        data.data.push({
          id: s.status.toString(),
          count: s.count,
          color: this.notificationStatusService.getRecipientResponseStatusColor(s.status),
          textColor: '#404040',
          label: s.label
        });
        dataItems.push(data);
      }
    });
    this.data = dataItems;
    this.changeDetector.detectChanges();
  }

  selected(data: { item: BarChartDataItem; subValue: any }): void {}
}
